/**
 * Métadonnées sociales (Open Graph + Twitter Card) partagées par toutes les routes.
 * Chaque page fournit son propre titre, sa propre description et sa propre image.
 */
import conseilTable from "@/assets/conseil-table.webp";
import conseilKeyframe from "@/assets/conseil-keyframe.webp";
import maieutePortrait from "@/assets/maieute-portrait.webp";
import memoImg from "@/assets/landing-v3/memo.jpg";
import secretairePortrait from "@/assets/secretaire-portrait.webp";

export const SITE_URL = "https://hojin.ai";

const abs = (src: string) => (src.startsWith("http") ? src : `${SITE_URL}${src}`);

/** Images de partage disponibles, résolues en URL absolue (hash de build inclus). */
export const OG_IMAGES = {
  conseil: abs(conseilTable),
  keyframe: abs(conseilKeyframe),
  maieute: abs(maieutePortrait),
  memo: abs(memoImg),
  secretaire: abs(secretairePortrait),
} as const;

export type OgImageKey = keyof typeof OG_IMAGES;

export interface SocialMetaInput {
  title: string;
  description: string;
  /** Chemin de la page, ex. "/pricing". */
  path?: string;
  /** URL absolue de la page (prioritaire sur `path`). */
  url?: string;
  /** Image de partage propre à la route. */
  image?: OgImageKey;
  type?: "website" | "article" | "profile";
  /** Passe la Twitter Card en vignette carrée (pages légales, utilitaires). */
  card?: "summary" | "summary_large_image";
}

export function socialMeta({
  title,
  description,
  path,
  url: absoluteUrl,
  image = "conseil",
  type = "website",
  card = "summary_large_image",
}: SocialMetaInput) {
  const url = absoluteUrl ?? `${SITE_URL}${path ?? "/"}`;
  const imageUrl = OG_IMAGES[image];
  return [
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:url", content: url },
    { property: "og:type", content: type },
    { property: "og:site_name", content: "Hōjin" },
    { property: "og:locale", content: "fr_FR" },
    { property: "og:image", content: imageUrl },
    { property: "og:image:alt", content: title },
    { name: "twitter:card", content: card },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: imageUrl },
  ];
}

/* -------------------------------------------------------------------------- */
/*  JSON-LD — données structurées partagées                                    */
/* -------------------------------------------------------------------------- */

export const ORG_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;

export interface PageJsonLdInput {
  /** Chemin de la page, ex. "/pricing". */
  path: string;
  name: string;
  description: string;
  /** WebPage par défaut ; Article pour du contenu éditorial. */
  type?: "WebPage" | "Article" | "AboutPage" | "CollectionPage";
  image?: OgImageKey;
  /** Fil d'Ariane après l'accueil, ex. [{ name: "Tarifs", path: "/pricing" }]. */
  breadcrumb?: { name: string; path: string }[];
  datePublished?: string;
  dateModified?: string;
}

/**
 * Graphe JSON-LD d'une page : Organization + WebSite (référencés partout),
 * la page elle-même, et un fil d'Ariane optionnel.
 * À passer dans `head().scripts`.
 */
export function pageJsonLd({
  path,
  name,
  description,
  type = "WebPage",
  image = "conseil",
  breadcrumb,
  datePublished,
  dateModified,
}: PageJsonLdInput) {
  const url = `${SITE_URL}${path}`;
  const isArticle = type === "Article";

  const page: Record<string, unknown> = {
    "@type": type,
    "@id": `${url}#webpage`,
    url,
    name,
    headline: isArticle ? name : undefined,
    description,
    inLanguage: "fr-FR",
    isPartOf: { "@id": WEBSITE_ID },
    image: OG_IMAGES[image],
    ...(isArticle
      ? {
          author: { "@id": ORG_ID },
          publisher: { "@id": ORG_ID },
          datePublished,
          dateModified: dateModified ?? datePublished,
        }
      : { about: { "@id": ORG_ID } }),
  };

  const graph: Record<string, unknown>[] = [
    {
      "@type": "Organization",
      "@id": ORG_ID,
      name: "Hōjin",
      legalName: "UDITEK SAS",
      url: `${SITE_URL}/`,
      logo: {
        "@type": "ImageObject",
        "@id": `${SITE_URL}/#logo`,
        url: `${SITE_URL}/favicon-512.png`,
      },
    },
    {
      "@type": "WebSite",
      "@id": WEBSITE_ID,
      name: "Hōjin",
      url: `${SITE_URL}/`,
      publisher: { "@id": ORG_ID },
      inLanguage: "fr-FR",
    },
    page,
  ];

  if (breadcrumb?.length) {
    graph.push({
      "@type": "BreadcrumbList",
      "@id": `${url}#breadcrumb`,
      itemListElement: [{ name: "Accueil", path: "/" }, ...breadcrumb].map((item, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: item.name,
        item: `${SITE_URL}${item.path}`,
      })),
    });
  }

  return [
    {
      type: "application/ld+json",
      children: JSON.stringify({ "@context": "https://schema.org", "@graph": graph }),
    },
  ];
}
